import { supabaseClient } from '../net/supabase-client.js?v=20260801-token-hash-1';
import { quoteEmeraldPurchase } from './emerald-pricing.mjs';

async function functionErrorMessage(error) {
  const response = error?.context;
  if (response && typeof response.json === 'function') {
    try {
      const payload = await response.json();
      if (payload?.error) return String(payload.error);
    } catch {}
  }
  return String(error?.message || error || 'checkout_unavailable');
}

export async function beginEmeraldCheckout(amount) {
  const quote = quoteEmeraldPurchase(amount);
  if (!quote.ok) throw new Error(quote.error);

  const { data, error } = await supabaseClient.functions.invoke('create-emerald-checkout', {
    body: {
      amount: quote.amount,
      requestId: crypto.randomUUID(),
    },
  });
  if (error) throw new Error(await functionErrorMessage(error));
  if (!data?.url || !data?.orderId) throw new Error('checkout_unavailable');
  location.assign(data.url);
  return data;
}

/** @deprecated Produtos premium devem usar purchasePremiumProduct — o cliente não é autoridade de preço. */
export async function spendEmerald(amount, reason, requestId = crypto.randomUUID()) {
  const value = Math.round(Number(amount));
  if (!Number.isInteger(value) || value < 1) throw new Error('invalid_emerald_spend');
  const { data, error } = await supabaseClient.rpc('spend_emerald', {
    p_amount: value,
    p_reason: String(reason || ''),
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function purchasePremiumProduct(productId, durationHours = null, requestId = crypto.randomUUID()) {
  const id = String(productId || '').trim();
  if (!id) throw new Error('invalid_premium_product');
  const payload = {
    p_product_id: id,
    p_request_id: requestId,
  };
  if (durationHours != null) payload.p_duration_hours = Math.round(Number(durationHours));
  const { data, error } = await supabaseClient.rpc('purchase_premium_product', payload);
  if (error) throw error;
  return data;
}

export async function activatePremiumBoost(boostId, requestId = crypto.randomUUID()) {
  const { data, error } = await supabaseClient.rpc('activate_premium_boost', {
    p_boost_id: boostId,
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function currentPremiumBoosts() {
  const { data, error } = await supabaseClient.rpc('get_my_premium_boosts');
  if (error) throw error;
  return data || { pending: [], active: [] };
}

export async function purchaseVip(planDays, requestId = crypto.randomUUID()) {
  const days = Math.round(Number(planDays));
  if (![30, 60, 90].includes(days)) throw new Error('invalid_vip_plan');
  const { data, error } = await supabaseClient.rpc('purchase_vip', {
    p_plan_days: days,
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function purchaseSeasonPass(seasonId = 'season-1', requestId = crypto.randomUUID()) {
  const { data, error } = await supabaseClient.rpc('purchase_season_pass', {
    p_season_id: seasonId,
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function claimSeasonTier(seasonId, tier, track, requestId = crypto.randomUUID()) {
  const { data, error } = await supabaseClient.rpc('claim_season_tier', {
    p_season_id: seasonId,
    p_tier: Math.round(Number(tier)),
    p_track: track === 'premium' ? 'premium' : 'free',
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function currentSeasonProgress(seasonId = 'season-1') {
  const { data, error } = await supabaseClient.rpc('get_my_season_progress', {
    p_season_id: seasonId,
  });
  if (error) throw error;
  return data;
}

export async function currentDailyQuests() {
  const { data, error } = await supabaseClient.rpc('get_my_daily_quests');
  if (error) throw error;
  return data;
}

export async function currentDailyGift() {
  const { data, error } = await supabaseClient.rpc('get_my_daily_gift');
  if (error) throw error;
  return data;
}

export async function claimDailyGift(requestId = crypto.randomUUID()) {
  const { data, error } = await supabaseClient.rpc('claim_daily_gift', {
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function claimDailyQuest(questId, requestId = crypto.randomUUID()) {
  const { data, error } = await supabaseClient.rpc('claim_daily_quest', {
    p_quest_id: String(questId || ''),
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function reportDailyQuestProgress(type, amount = 1) {
  const { data, error } = await supabaseClient.rpc('report_daily_quest_progress', {
    p_type: String(type || ''),
    p_amount: Math.max(1, Math.floor(Number(amount) || 1)),
  });
  if (error) throw error;
  return data;
}

export async function claimRaidRewards(raidId, reward, requestId = crypto.randomUUID()) {
  const { data, error } = await supabaseClient.rpc('claim_raid_rewards', {
    p_raid_id: String(raidId || ''),
    p_reward: reward && typeof reward === 'object' ? reward : {},
    p_request_id: requestId,
  });
  if (error) throw error;
  return data;
}

export async function currentPremiumEntitlements() {
  const { data, error } = await supabaseClient.rpc('get_my_premium_entitlements');
  if (error) throw error;
  return data || { vip_expires_at: null, vip_active: false };
}

export async function emeraldCheckoutStatus(sessionId) {
  const value = String(sessionId || '');
  if (!value.startsWith('cs_') || value.length > 255) throw new Error('invalid_checkout_session');
  const { data, error } = await supabaseClient
    .from('emerald_orders')
    .select('id,status,emerald_amount,total_cents,currency,paid_at')
    .eq('stripe_checkout_session_id', value)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}
